import { Box } from '@mui/material';
import { useStyles } from './SessionTreatmentPlan.styles';
import { Icons } from '../../../../../../icons';
import dayjs from '../../../../../../../utils/dayjs';
import TreatmentPlanAnimation from '../../../../../../icons/lotties/lottieIcons/TreatmentPlanAnimation';
import { colors } from '../../../../../../colors';
import LoveCare from '../../../../../../icons/lotties/lottieIcons/LoveCare';

const SessionTreatmentPlan = ({ treatmentPlan, modalities }) => {
  const classes = useStyles();

  if (!treatmentPlan) {
    return null;
  }
  const expiryDate = dayjs(treatmentPlan.expiryDate);
  const now = dayjs();
  const expiresInDays = Math.abs(expiryDate.diff(now, 'day'));
  const isExpired = expiryDate.isBefore(now);

  return (
    <Box className={classes.container}>
      <Box className={classes.welcomeFrame}>
        <Box display="flex" flexDirection="column" flexGrow={1} flex={1} gap="4px">
          <p className={classes.sectionTitle}>
            Treatment Plan
          </p>
          <p className={classes.sectionSubtitle}>
            Read out the goals & make sure it's relevant
          </p>
        </Box>
        <TreatmentPlanAnimation className={classes.animatedIcon} />
      </Box>
      <Box className={classes.askBox}>
        <Box className={classes.contentHeader}>
          <Icons glyph={'question-outlined'} className={classes.askIcon} />
          <p className={classes.firstLine}>
            You may want to <span style={{fontWeight: 700, color: colors.secondary600}}>ask</span> things like:
          </p>
        </Box>
        <Box className={classes.askContent}>
          <Box className={classes.instruction} sx={{ backgroundColor: colors.secondary100}}>
            Are these goals still relevant?
          </Box>
          <Box className={classes.instruction} sx={{ backgroundColor: colors.secondary100}}>
            Are there any changes you'd like to make?
          </Box>
        </Box>
      </Box>
      <Box className={classes.planBox}>
        <Box className={classes.planBoxHeader}>
          <Box display="flex" flexDirection="column" gap="4px">
            <p className={classes.planBoxTitle}>{treatmentPlan.name}</p>
            <p className={classes.planBoxSubtitle}>
              {isExpired ? `Expired ${expiresInDays} days ago` : `Expires in ${expiresInDays} days`}
            </p>
          </Box>
        </Box>
        <Box className={classes.planContent}>
          {treatmentPlan.problems?.map((problem, index) => (
            <Box key={problem.id} className={classes.problemBox}>
              <Box className={classes.problemHeader}>
                <Box className={classes.problemTitle}>Problem {index + 1}</Box>
                <Box className={classes.problemDetail}>{problem.detail}</Box>
              </Box>
              {problem.goals?.map((goal, goalIndex) => (
                <Box key={goal.id} className={classes.goalBox}>
                  <Box className={classes.goalHeader}>Goal {goalIndex + 1} -- {goal.name}</Box>
                  <Box className={classes.goalContent}>
                    <Box className={classes.goalDescription}>{goal.details}</Box>
                    <Box className={classes.objective} width="100%">
                      <Box display="flex" flexDirection="row" gap="8px" alignItems="center">
                        <Box className={classes.objectiveTile}>Objectives</Box>
                      </Box>
                      <Box display="flex" flexDirection="column" gap="8px">
                        {goal.objectives.length > 0 ? (
                          goal.objectives?.map((item, index) => (
                            <Box key={item.id} className={classes.objectivesListItem}>
                              <Icons glyph={item.isCompleted ? 'check-circle' : 'checked-circle-outlined'} className={classes.objectivesCheckIcon} />
                              <Box>{item.details}</Box>
                            </Box>
                          ))
                        ) : (
                          <Box className={classes.objectivesListItem}>
                            <Box>No objectives found</Box>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          ))}
          <Box className={classes.modalitiesBox}>
            <Box className={classes.problemTitle}>Treatment plan modalities</Box>
            <Box display="flex" flexDirection="column" gap="8px">
              {modalities && modalities.length > 0 ? (
                modalities.map((item, index) => (
                  <Box key={item.id} className={classes.objectivesListItem}>
                    <Icons glyph={'check-circle'} className={classes.objectivesCheckIcon} />
                    <Box>{item}</Box>
                  </Box>
                ))
              ) : (
                <Box className={classes.objectivesListItem}>
                  <Box>No treatment modalities</Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export { SessionTreatmentPlan };
